import { ApiError, createApiErrorFromResponse } from "../api";
import { joinUrlPath } from "../url";
import { parseJsonResponse, readApiErrorDetail, readOptionalStringToken } from "./response";
import type { ResolveOidcClientIdConfig } from "./types";

type IdentityApplicationPublicConfigRead = {
  oidc_client_id?: string | null;
};

export function createOidcClientIdResolver(config: ResolveOidcClientIdConfig) {
  const cacheTtlMs = config.cacheTtlMs ?? 5 * 60 * 1000;
  let cachedClientId: { expiresAt: number; value: string } | null = null;
  let resolvePromise: Promise<string> | null = null;

  function reset() {
    cachedClientId = null;
    resolvePromise = null;
    config.onReset?.();
  }

  async function resolveClientId(
    fetchImpl: typeof fetch = fetch,
    forceRefresh = false,
  ): Promise<string> {
    if (!forceRefresh && cachedClientId && cachedClientId.expiresAt > Date.now()) {
      return cachedClientId.value;
    }
    if (resolvePromise) {
      return await resolvePromise;
    }
    resolvePromise = (async () => {
      const response = await fetchImpl(
        joinUrlPath(
          config.ssoApiBaseUrl,
          `/api/identity/public-applications/${encodeURIComponent(config.appKey)}`,
        ),
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!response.ok) {
        const detail = await readApiErrorDetail(response);
        throw createApiErrorFromResponse(response, detail);
      }
      const payload = await parseJsonResponse<IdentityApplicationPublicConfigRead>(response);
      const clientId = readOptionalStringToken(payload?.oidc_client_id, {
        fieldName: "oidc_client_id",
        invalidCode: "sso.oidc_client_id_invalid",
        invalidMessage: `SSO OIDC client id has invalid type for app_key=${config.appKey}`,
      });
      if (!clientId) {
        throw new ApiError(500, {
          code: "sso.oidc_client_id_missing",
          message: `SSO OIDC client id is not configured for app_key=${config.appKey}`,
        });
      }
      cachedClientId = {
        value: clientId,
        expiresAt: Date.now() + cacheTtlMs,
      };
      return clientId;
    })();
    try {
      return await resolvePromise;
    } catch (error) {
      reset();
      throw error;
    } finally {
      resolvePromise = null;
    }
  }

  return {
    reset,
    resolveClientId,
  };
}
